import {
  groupSettlementsByShip,
  groupSettlementsByWeek,
  sortWeekKeys,
} from 'common/src/utils/functions';
import { Settlement } from 'common/src/utils/types';
import { format, getWeek } from 'date-fns';
import * as XLSX from 'xlsx';

export interface GroupedSettlementDetail {
  kilos: number;
  fob: number;
  fobPerKg: number;
  totalRevenue: number;
  totalCommission: number;
  totalTax: number;
  totalOtherCosts: number;
  totalFreight: number;
}

export interface GroupedSettlement {
  mainClient: {
    totalKilos: number;
    totalFob: number;
    details: {
      [key: string]: GroupedSettlementDetail;
    };
    dates: string[];
  };
  comparisons: {
    [clientId: string]: {
      totalKilos: number;
      totalFob: number;
      details: {
        [key: string]: GroupedSettlementDetail;
      };
      dates: string[];
    };
  };
  meta?: {
    eta?: string;
    etd?: string;
    shipName?: string;
    marketName?: string;
  };
}

export interface VarietyStats {
  variety: string;
  totalKilos: number;
  totalDifference: number;
  perKiloDifference: number;
}

export interface ShipmentCompensation {
  shipmentName: string;
  totalKilos: number;
  totalFob: number;
  compensationPerKg: number;
  totalCompensation: number;
}

export interface ExcelRowData {
  cliente: string;
  variedad: string;
  formato: string;
  calibre: string;
  color: string;
  'Sum of FOB KG': number;
  'Sum of Kilos': number;
}

interface ExcelDetail {
  nave: string;
  variedad: string;
  formato: string;
  unit_weight: string;
  calibre: string;
  color: string;
  etiqueta: string;
  mainFob: number;
  mainKilos: number;
  mainFobPerKg: number;
  othersFob: number;
  othersKilos: number;
  othersFobPerKg: number;
  difference: number;
  totalDifference: number;
}

interface ExcelShipTotals {
  mainClientTotalFob: number;
  mainClientTotalKilos: number;
  totalOthersFob: number;
  totalOthersKilos: number;
  totalShipDifference: number;
}

// New interface for the main client detail with comparable data
export interface MainClientDetailWithComparable {
  reference_number: string;
  groupKey: string;          // Ship or week key
  productKey: string;        // The detail key (variety-caliber-etc.)
  client?: string;
  destinationCountry?: string;
  shipTripId?: string;
  variety?: string;
  caliber?: string;
  kilos: number;
  fob: number;
  fobPerKg: number;
  settlement_detail_id: number;
  comparisonFobPerKg: number | null; // Comparable FOB/KG if available (median)
  hasComparison: boolean;    // Whether there's comparable data
  comparableKilos: number;   // Total comparable kilos
  comparableFob: number;     // Total comparable FOB
  differenceUSD: number;
  comparisonQ1FobPerKg?: number | null;  // Industry Q1 FOB/KG
  comparisonQ3FobPerKg?: number | null;  // Industry Q3 FOB/KG
  differenceQ1USD?: number;              // Diff vs Q1 in USD
  differenceQ3USD?: number;              // Diff vs Q3 in USD
  industryFiveNum?: { min: number; q1: number; median: number; q3: number; max: number } | null;
}

const getRevenueTotal = (detail: any, useLocalCurrency: boolean): number => {
  const revenues = detail.settlement_detail_revenues;
  if (!revenues || !Array.isArray(revenues) || revenues.length === 0) return 0;
  return revenues.reduce((sum: number, r: any) => {
    if (useLocalCurrency) {
      return sum + (r.amount_local || 0);
    }
    return sum + (r.amount_usd || 0);
  }, 0);
};

const getDetailValue = (
  detail: any,
  settlement: any,
  priceType: 'FOB' | 'CIF',
  useLocalCurrency: boolean,
  includeRevenues = false
): number => {
  let base = 0;
  if (useLocalCurrency) {
    // Net local = revenue - tax - commission - other costs
    const netLocal =
      (detail.local_total_price || 0) -
      (detail.local_total_tax || 0) -
      (detail.local_total_commision || 0) -
      (detail.local_other_costs || 0);
    if (priceType === 'CIF') {
      base = netLocal;
    } else {
      // FOB: subtract freight converted to local currency
      const freightLocal = (detail.usd_freight || 0) * (detail.exchange_rate || 1);
      base = netLocal - freightLocal;
    }
  } else {
    // USD mode (existing logic)
    const fob = detail.usd_total_fob;
    const totalSettlementKilos = settlement.settlement_details.reduce(
      (acc: number, d: any) => acc + d.kilos,
      0
    );
    const freightPerKg = settlement.total_freight_usd
      ? settlement.total_freight_usd / totalSettlementKilos
      : 0;
    base = priceType === 'CIF' ? fob + detail.kilos * freightPerKg : fob;
  }

  if (includeRevenues) {
    base += getRevenueTotal(detail, useLocalCurrency);
  }

  return base;
};

interface DetailComponents {
  revenue: number;
  commission: number;
  tax: number;
  otherCosts: number;
  freight: number;
}

const getDetailComponents = (
  detail: any,
  settlement: any,
  priceType: 'FOB' | 'CIF',
  useLocalCurrency: boolean
): DetailComponents => {
  if (useLocalCurrency) {
    const revenue = detail.local_total_price || 0;
    const commission = detail.local_total_commision || 0;
    const tax = detail.local_total_tax || 0;
    const otherCosts = detail.local_other_costs || 0;
    const freight =
      priceType === 'FOB'
        ? (detail.usd_freight || 0) * (detail.exchange_rate || 1)
        : 0;
    return { revenue, commission, tax, otherCosts, freight };
  }
  // USD mode
  const exchangeRate = detail.exchange_rate || 1;
  const revenue = (detail.local_total_price || 0) / exchangeRate;
  const commission = (detail.local_total_commision || 0) / exchangeRate;
  const tax = (detail.local_total_tax || 0) / exchangeRate;
  const otherCosts = (detail.local_other_costs || 0) / exchangeRate;
  if (priceType === 'FOB') {
    const totalSettlementKilos = settlement.settlement_details.reduce(
      (acc: number, d: any) => acc + d.kilos,
      0
    );
    const freightPerKg = settlement.total_freight_usd
      ? settlement.total_freight_usd / totalSettlementKilos
      : 0;
    const freight = detail.kilos * freightPerKg;
    return { revenue, commission, tax, otherCosts, freight };
  }
  return { revenue, commission, tax, otherCosts, freight: 0 };
};

export class ComparativeClientsUtility {
  static groupSettlements(
    settlements: Settlement[],
    mainClientId: string,
    comparisonClientIds: string[],
    isFormatEnabled: boolean,
    isLabelEnabled: boolean,
    isColorEnabled: boolean,
    priceType: 'FOB' | 'CIF' = 'FOB',
    dateGroupingType: 'date_sold' | 'ship_eta' | 'ship_etd' = 'date_sold',
    comparisonType: 'custom' | 'byShips' | 'byWeeks',
    useLocalCurrency = false,
    includeRevenuesMain = false,
    includeRevenuesComparison = false,
    includeMarketInAir = false
  ) {
    // Filter out settlements without exchange rate
    const validSettlements = settlements.filter(
      (settlement) =>
        settlement.exchange_rate !== null &&
        settlement.exchange_rate !== undefined
    );

    // Get the date to use for grouping
    const getGroupingDate = (settlement: Settlement) => {
      if (dateGroupingType === 'ship_eta') {
        return settlement.ship_trip?.eta;
      } else if (dateGroupingType === 'ship_etd') {
        return settlement.ship_trip?.etd ;
      } else if (dateGroupingType === 'date_sold') {
        return settlement.date_sold;
      }
    };

    // Use utility functions to group settlements with the selected date
    const byShips = groupSettlementsByShip(validSettlements, getGroupingDate, { includeMarketInAir });
    const byWeeks = groupSettlementsByWeek(validSettlements, getGroupingDate);

    // console.log('byShips', byShips);
    // console.log('byWeeks', byWeeks);

    // Detect if we're in custom mode from the parameter or by checking settlement name
    const useCustomMode =
      comparisonType == 'custom' ||
      (validSettlements.length > 0 &&
        validSettlements.every(
          (s) => s.ship_trip?.ship?.name === 'COMPARACION_TOTAL'
        ));

    // Create an array to store ungrouped main client details
    const mainClientDetails: MainClientDetailWithComparable[] = [];

    // Convert grouped settlements to GroupedSettlement format
    const processGroups = (groups: Record<string, Settlement[]>, type: 'byShips' | 'byWeeks') => {
      const result: { [key: string]: GroupedSettlement } = {};

      // First process comparison clients to have their data ready for comparison
      Object.entries(groups).forEach(([key, groupSettlements]) => {
        result[key] = {
          mainClient: {
            totalKilos: 0,
            totalFob: 0,
            details: {},
            dates: [],
          },
          comparisons: {},
          meta: {},
        };

        // Capture ship meta (eta/etd/ship) if available in this group
        try {
          const settlementsArr = groupSettlements as Settlement[];
          if (settlementsArr && settlementsArr.length > 0) {
            const withEta = settlementsArr.filter((s) => s?.ship_trip?.eta);
            if (withEta.length > 0) {
              const minEta = withEta
                .map((s) => s.ship_trip!.eta)
                .reduce((min, cur) => (new Date(cur) < new Date(min) ? cur : min));
              result[key].meta!.eta = minEta;
            }
            const withEtd = settlementsArr.filter((s) => s?.ship_trip?.etd);
            if (withEtd.length > 0) {
              const minEtd = withEtd
                .map((s) => s.ship_trip!.etd)
                .reduce((min, cur) => (new Date(cur) < new Date(min) ? cur : min));
              result[key].meta!.etd = minEtd;
            }
            result[key].meta!.shipName =
              settlementsArr[0]?.ship_trip?.ship?.name || undefined;
            // Store market name for AIR keys
            if (key.startsWith('AIR-') && key.includes('-M')) {
              const marketSettlement = settlementsArr.find(s => s.market?.name);
              if (marketSettlement) {
                result[key].meta!.marketName = marketSettlement.market.name;
              }
            }
          }
        } catch {}


        // First pass: Process comparison clients data
        groupSettlements.forEach((settlement: any) => {
          // Skip main client in this pass - we'll handle it in the second pass
          if (settlement.receiver === mainClientId && !useCustomMode) {
            return;
          }
          if (settlement._isMainSelection && useCustomMode) {
            return;
          }

          // Only process if it's a comparison client
          const isComparisonClient = 
            useCustomMode 
              ? settlement._isComparisonSelection
              : comparisonClientIds.includes('all') || 
                comparisonClientIds.includes(settlement.receiver);

          if (!isComparisonClient) {
            return;
          }

          settlement.settlement_details?.forEach((detail: any) => {
            if (!detail.variety || !detail.caliber) return;
            // Only check for color if it's enabled in the filtering
            if (isColorEnabled && !detail.color) return;

            let detailKey = `${detail.variety}-${detail.caliber}`;
            if (isColorEnabled) {
              detailKey += `-${detail.color}`;
            }
            if (isFormatEnabled && isLabelEnabled) {
              detailKey += `-${detail.format.replace(
                /\s+/g,
                '_'
              )}-${detail.unit_weight
                .toString()
                .replace('.', '_')}-${detail.label.replace(/\s+/g, '_')}`;
            } else if (isFormatEnabled) {
              detailKey += `-${detail.format.replace(
                /\s+/g,
                '_'
              )}-${detail.unit_weight.toString().replace('.', '_')}`;
            } else if (isLabelEnabled) {
              detailKey += `-${detail.label.replace(/\s+/g, '_')}`;
            }

            // Calculate total value based on price type and currency mode
            const totalValue = getDetailValue(detail, settlement, priceType, useLocalCurrency, includeRevenuesComparison);

            if (!result[key].comparisons[settlement.receiver]) {
              result[key].comparisons[settlement.receiver] = {
                totalKilos: 0,
                totalFob: 0,
                details: {},
                dates: [],
              };
            }
            const clientComparison = result[key].comparisons[settlement.receiver];

            if (!clientComparison.details[detailKey]) {
              clientComparison.details[detailKey] = {
                kilos: 0,
                fob: 0,
                fobPerKg: 0,
                totalRevenue: 0,
                totalCommission: 0,
                totalTax: 0,
                totalOtherCosts: 0,
                totalFreight: 0,
              };
            }
            const compComponents = getDetailComponents(detail, settlement, priceType, useLocalCurrency);
            clientComparison.details[detailKey].kilos += detail.kilos;
            clientComparison.details[detailKey].fob += totalValue;
            clientComparison.details[detailKey].totalRevenue += compComponents.revenue;
            clientComparison.details[detailKey].totalCommission += compComponents.commission;
            clientComparison.details[detailKey].totalTax += compComponents.tax;
            clientComparison.details[detailKey].totalOtherCosts += compComponents.otherCosts;
            clientComparison.details[detailKey].totalFreight += compComponents.freight;
            clientComparison.totalKilos += detail.kilos;
            clientComparison.totalFob += totalValue;
            if (!clientComparison.dates.includes(settlement.date_sold)) {
              clientComparison.dates.push(settlement.date_sold);
            }
          });
        });

        // Calculate FOB per kg for comparison clients
        Object.values(result[key].comparisons).forEach((client) => {
          Object.values(client.details).forEach((detail) => {
            detail.fobPerKg = detail.kilos > 0 ? detail.fob / detail.kilos : 0;
          });
        });

        if ((comparisonType == "byShips" || comparisonType == "custom") && type === 'byShips' && type === 'byShips' || comparisonType == "byWeeks" && type === 'byWeeks') {
          
        // Second pass: Process main client data
        groupSettlements.forEach((settlement: any) => {
          const isMainClient = 
            useCustomMode 
              ? settlement._isMainSelection 
              : settlement.receiver === mainClientId;
          
          if (!isMainClient) {
            return;
          }

          // console.log('settlement', settlement);

          settlement.settlement_details?.forEach((detail: any) => {
            if (!detail.variety || !detail.caliber) return;
            // Only check for color if it's enabled in the filtering
            if (isColorEnabled && !detail.color) return;

            let detailKey = `${detail.variety}-${detail.caliber}`;
            if (isColorEnabled) {
              detailKey += `-${detail.color}`;
            }
            if (isFormatEnabled && isLabelEnabled) {
              detailKey += `-${detail.format.replace(
                /\s+/g,
                '_'
              )}-${detail.unit_weight
                .toString()
                .replace('.', '_')}-${detail.label.replace(/\s+/g, '_')}`;
            } else if (isFormatEnabled) {
              detailKey += `-${detail.format.replace(
                /\s+/g,
                '_'
              )}-${detail.unit_weight.toString().replace('.', '_')}`;
            } else if (isLabelEnabled) {
              detailKey += `-${detail.label.replace(/\s+/g, '_')}`;
            }

            // Calculate total value based on price type and currency mode
            const totalValue = getDetailValue(detail, settlement, priceType, useLocalCurrency, includeRevenuesMain);

            if (!result[key].mainClient.details[detailKey]) {
              result[key].mainClient.details[detailKey] = {
                kilos: 0,
                fob: 0,
                fobPerKg: 0,
                totalRevenue: 0,
                totalCommission: 0,
                totalTax: 0,
                totalOtherCosts: 0,
                totalFreight: 0,
              };
            }
            const mainComponents = getDetailComponents(detail, settlement, priceType, useLocalCurrency);
            result[key].mainClient.details[detailKey].kilos += detail.kilos;
            result[key].mainClient.details[detailKey].fob += totalValue;
            result[key].mainClient.details[detailKey].totalRevenue += mainComponents.revenue;
            result[key].mainClient.details[detailKey].totalCommission += mainComponents.commission;
            result[key].mainClient.details[detailKey].totalTax += mainComponents.tax;
            result[key].mainClient.details[detailKey].totalOtherCosts += mainComponents.otherCosts;
            result[key].mainClient.details[detailKey].totalFreight += mainComponents.freight;
            result[key].mainClient.totalKilos += detail.kilos;
            result[key].mainClient.totalFob += totalValue;
            if (!result[key].mainClient.dates.includes(settlement.date_sold)) {
              result[key].mainClient.dates.push(settlement.date_sold);
            }

            // Now gather comparison data for this detail
            let totalComparisonKilos = 0;
            let totalComparisonFob = 0;

            Object.values(result[key].comparisons).forEach((client) => {
              if (client.details[detailKey]) {
                totalComparisonKilos += client.details[detailKey].kilos;
                totalComparisonFob += client.details[detailKey].fob;
              }
            });

            // Create a MainClientDetailWithComparable entry
            const comparisonFobPerKg = totalComparisonKilos > 0 
              ? totalComparisonFob / totalComparisonKilos 
              : null;

            let fobPerKg = detail.kilos > 0 ? totalValue / detail.kilos : 0;

            mainClientDetails.push({
              reference_number: settlement.reference_number,
              groupKey: key,
              productKey: detailKey,
              kilos: detail.kilos,
              fob: totalValue,
              fobPerKg,
              settlement_detail_id: detail.id,
              comparisonFobPerKg,
              hasComparison: totalComparisonKilos > 0,
              comparableKilos: totalComparisonKilos,
              comparableFob: totalComparisonFob,
              differenceUSD: comparisonFobPerKg ? detail.kilos * (fobPerKg - comparisonFobPerKg ) : 0,
            });
          });
        });}

        // Calculate FOB per kg for main client details
        Object.values(result[key].mainClient.details).forEach((detail) => {
          detail.fobPerKg = detail.kilos > 0 ? detail.fob / detail.kilos : 0;
        });
      });

      return result;
    };

    return {
      byShips: processGroups(byShips, "byShips"),
      byWeeks: processGroups(byWeeks, "byWeeks"),
      mainClientDetails // Return the ungrouped details
    };
  }

  static calculateSummaryStatistics(groupedData: {
    [key: string]: GroupedSettlement;
  }) {
    let totalDifference = 0;
    let mainClientTotalKilos = 0;
    const varietyStats: { [key: string]: VarietyStats } = {};

    // Track total kilos and comparable kilos for percentage
    let totalKilos = 0;
    let comparableKilos = 0;

    Object.values(groupedData).forEach((group) => {
      Object.entries(group.mainClient.details).forEach(
        ([detailKey, mainDetail]) => {
          const variety = detailKey.split('-')[0];

          // Add to total kilos regardless of comparison
          totalKilos += mainDetail.kilos;

          // Sumar todos los kilos y FOB de los clientes de comparación para este detalle
          let totalComparisonKilos = 0;
          let totalComparisonFob = 0;

          Object.values(group.comparisons).forEach((client) => {
            if (client.details[detailKey]) {
              totalComparisonKilos += client.details[detailKey].kilos;
              totalComparisonFob += client.details[detailKey].fob;
            }
          });

          if (totalComparisonKilos > 0) {
            if (!varietyStats[variety]) {
              varietyStats[variety] = {
                variety,
                totalKilos: 0,
                totalDifference: 0,
                perKiloDifference: 0,
              };
            }

            comparableKilos += mainDetail.kilos;

            const mainFobPerKg = mainDetail.fob / mainDetail.kilos;
            const avgComparisonFobPerKg = totalComparisonFob / totalComparisonKilos;
            const difference = (mainFobPerKg - avgComparisonFobPerKg) * mainDetail.kilos;

            totalDifference += difference;
            varietyStats[variety].totalDifference += difference;
            varietyStats[variety].totalKilos += mainDetail.kilos;
            mainClientTotalKilos += mainDetail.kilos;
          }
        }
      );
    });

    // Calcular diferencia por kilo para cada variedad
    Object.values(varietyStats).forEach((stats) => {
      stats.perKiloDifference =
        stats.totalKilos > 0
          ? stats.totalDifference / stats.totalKilos
          : 0;
    });

    // Calculate comparable percentage
    const comparablePercentage =
      totalKilos > 0
        ? Math.round((comparableKilos / totalKilos) * 100)
        : 0;

    return {
      totalFobDifference: totalDifference,
      perKiloDifference:
        mainClientTotalKilos > 0
          ? totalDifference / mainClientTotalKilos
          : 0,
      comparablePercentage,
      mainClient: {
        totalKilos: mainClientTotalKilos,
      },
      varietyStats: Object.values(varietyStats),
    };
  }

  

  static prepareDataForExcel(
    shipmentData: { [key: string]: GroupedSettlement },
    selectedClient: string,
    isFormatEnabled: boolean,
    isLabelEnabled: boolean,
    isColorEnabled: boolean,
    priceType: 'FOB' | 'CIF' = 'FOB',
    currencyLabel: string = 'USD'
  ) {
    const excelData: any[][] = [];
    let grandTotalDifference = 0;

    // Encabezados
    const headers = [
      'Nave/Semana',
      'Variedad',
      'Calibre',
      ...(isColorEnabled ? ['Color'] : []),
      ...(isFormatEnabled ? ['Formato', 'Peso Unitario'] : []),
      ...(isLabelEnabled ? ['Etiqueta'] : []),
      `${selectedClient} ${currencyLabel}/KG`,
      `${selectedClient} Kilos`,
      `OTROS ${currencyLabel}/KG`,
      'OTROS Kilos',
      `Diferencia ${currencyLabel}/KG`,
      'Diferencia Total',
    ];

    excelData.push(headers);

    // Ordenar las entradas
    const entries = Object.entries(shipmentData);

    interface ShipEntry {
      name: string;
      data: GroupedSettlement;
      originalKey: string;
      week?: number;
    }

    const processedEntries: ShipEntry[] = entries
      .map(([key, data]) => {
        // Si es una semana (formato: WEEK-2024-W51)
        if (key.match(/^WEEK-\d{4}-W\d+$/)) {
          const [_, year, weekNum] = key.match(/^WEEK-(\d{4})-W(\d+)$/) || [];

          return {
            name: `${year} - Semana ${weekNum}`,
            data: data as GroupedSettlement,
            originalKey: key,
            week: parseInt(weekNum),
          };
        }
        // Si es un envío aéreo (formato: AIR-W{XX} or AIR-W{XX}-M{marketId})
        else if (key.startsWith('AIR-')) {
          const week = key.match(/-W(\d+)/)?.[1] || '';
          const groupedData = data as GroupedSettlement;
          let displayName = `AEREO - Semana ${week}`;
          if (key.includes('-M') && groupedData.meta?.marketName) {
            displayName = `AEREO - Semana ${week} - ${groupedData.meta.marketName}`;
          }
          return {
            name: displayName,
            data: groupedData,
            originalKey: key,
            week: parseInt(week),
          };
        }
        // Si es un envío terrestre (formato: TER-W{XX})
        else if (key.startsWith('GROUND-')) {
          const week = key.split('-W')[1];
          return {
            name: `TERRESTRE - Semana ${week}`,
            data: data as GroupedSettlement,
            originalKey: key,
            week: parseInt(week),
          };
        }
        // Si es un envío marítimo (formato: SHIP-{nombre}-{fecha})
        else if (key.startsWith('SHIP-')) {
          const parts = key.split('-');
          const shipName = parts.length > 1 ? parts[1].replace(/_/g, ' ') : key;
          return {
            name: shipName,
            data: data as GroupedSettlement,
            originalKey: key,
          };
        }
        // Para cualquier otro formato
        return {
          name: key,
          data: data as GroupedSettlement,
          originalKey: key,
        };
      })
      .sort((a, b) => {
        // Si ambos tienen semana, ordenar por año y semana (descendente)
        if (a.week !== undefined && b.week !== undefined) {
          return sortWeekKeys(b.originalKey, a.originalKey);
        }
        // Si no tienen semana, ordenar por clave original (descendente)
        return b.originalKey.localeCompare(a.originalKey);
      });

    // Procesar datos por entrada
    processedEntries.forEach((entry) => {
      const groupedDetails = new Map<string, ExcelDetail>();
      let totalShipDifference = 0;
      let hasValidComparisons = false;

      // Procesar detalles del cliente principal
      Object.entries(entry.data.mainClient.details).forEach(
        ([detailKey, detail]) => {
          const parts = detailKey.split('-');

          // Handle both cases: with and without color
          let variety, caliber, color;
          let restParts: string[] = [];

          if (isColorEnabled) {
            [variety, caliber, color, ...restParts] = parts;
          } else {
            [variety, caliber, ...restParts] = parts;
            color = ''; // empty color when disabled
          }

          let format = '';
          let unit_weight = '';
          let label = '';

          if (isFormatEnabled && isLabelEnabled && restParts.length >= 3) {
            format = restParts[0];
            unit_weight = restParts[1].replace(/_/g, '.');
            label = restParts[2];
          } else if (isFormatEnabled && restParts.length >= 2) {
            format = restParts[0];
            unit_weight = restParts[1].replace(/_/g, '.');
          } else if (isLabelEnabled && restParts.length >= 1) {
            label = restParts[0];
          }

          // Build key with or without color based on isColorEnabled
          const key = isColorEnabled
            ? `${variety}-${caliber}-${color}-${format}-${unit_weight}-${label}`
            : `${variety}-${caliber}-${format}-${unit_weight}-${label}`;

          // Solo agregar si hay datos del cliente principal
          if (detail.kilos > 0) {
            if (!groupedDetails.has(key)) {
              groupedDetails.set(key, {
                nave: entry.name,
                variedad: variety,
                formato: format,
                unit_weight: unit_weight,
                calibre: caliber,
                color: color,
                etiqueta: label,
                mainFob: Number(detail.fob),
                mainKilos: Number(detail.kilos),
                mainFobPerKg: Number((detail.fob / detail.kilos)),
                othersFob: 0,
                othersKilos: 0,
                othersFobPerKg: 0,
                difference: 0,
                totalDifference: 0,
              });
            } else {
              const existing = groupedDetails.get(key)!;
              existing.mainFob = Number(
                (existing.mainFob + detail.fob)
              );
              existing.mainKilos = Number(
                (existing.mainKilos + detail.kilos)
              );
              existing.mainFobPerKg = Number(
                (existing.mainFob / existing.mainKilos)
              );
            }
          }
        }
      );

      // Procesar datos de comparación
      Object.entries(entry.data.comparisons).forEach(
        ([clientId, clientData]) => {
          if (clientData && clientData.details) {
            Object.entries(clientData.details).forEach(
              ([detailKey, detail]) => {
                const parts = detailKey.split('-');

                // Handle both cases: with and without color
                let variety, caliber, color;
                let restParts: string[] = [];

                if (isColorEnabled) {
                  [variety, caliber, color, ...restParts] = parts;
                } else {
                  [variety, caliber, ...restParts] = parts;
                  color = ''; // empty color when disabled
                }

                let format = '';
                let unit_weight = '';
                let label = '';

                if (
                  isFormatEnabled &&
                  isLabelEnabled &&
                  restParts.length >= 3
                ) {
                  format = restParts[0];
                  unit_weight = restParts[1].replace(/_/g, '.');
                  label = restParts[2];
                } else if (isFormatEnabled && restParts.length >= 2) {
                  format = restParts[0];
                  unit_weight = restParts[1].replace(/_/g, '.');
                } else if (isLabelEnabled && restParts.length >= 1) {
                  label = restParts[0];
                }

                // Build key with or without color based on isColorEnabled
                const key = isColorEnabled
                  ? `${variety}-${caliber}-${color}-${format}-${unit_weight}-${label}`
                  : `${variety}-${caliber}-${format}-${unit_weight}-${label}`;

                if (detail.kilos > 0) {
                  if (!groupedDetails.has(key)) {
                    groupedDetails.set(key, {
                      nave: entry.name,
                      variedad: variety,
                      formato: format,
                      unit_weight: unit_weight,
                      calibre: caliber,
                      color: color,
                      etiqueta: label,
                      mainFob: 0,
                      mainKilos: 0,
                      mainFobPerKg: 0,
                      othersFob: Number(detail.fob),
                      othersKilos: Number(detail.kilos),
                      othersFobPerKg: 0,
                      difference: 0,
                      totalDifference: 0,
                    });
                  } else {
                    const existing = groupedDetails.get(key)!;
                    existing.othersFob = Number(
                      (existing.othersFob + detail.fob)
                    );
                    existing.othersKilos = Number(
                      (existing.othersKilos + detail.kilos)
                    );
                  }
                }
              }
            );
          }
        }
      );

      // Filtrar y calcular FOB/KG y diferencias
      const validDetails = new Map<string, ExcelDetail>();
      groupedDetails.forEach((group, key) => {
        // Si tiene datos del cliente principal, siempre lo incluimos
        if (group.mainKilos > 0) {
          // Si hay datos de comparación, calculamos las diferencias
          if (group.othersKilos > 0) {
            group.othersFobPerKg = Number(
              (group.othersFob / group.othersKilos)
            );
            group.difference = Number(
              (group.mainFobPerKg - group.othersFobPerKg)
            );
            group.totalDifference = Number(
              (group.difference * group.mainKilos)
            );
            totalShipDifference = Number(
              (totalShipDifference + group.totalDifference)
            );
          }
          validDetails.set(key, group);
          hasValidComparisons = true;
        }
      });

      // Solo agregar datos al Excel si hay datos del cliente principal
      if (hasValidComparisons) {
        validDetails.forEach((group) => {
          const baseRow = [group.nave, group.variedad, group.calibre];

          if (isColorEnabled) baseRow.push(group.color);
          if (isFormatEnabled) {
            baseRow.push(group.formato);
            baseRow.push(group.unit_weight);
          }
          if (isLabelEnabled) baseRow.push(group.etiqueta);

          const row = [
            ...baseRow,
            group.mainFobPerKg,
            group.mainKilos,
            group.othersKilos > 0 ? group.othersFobPerKg : null,
            group.othersKilos > 0 ? group.othersKilos : null,
            group.othersKilos > 0 ? group.difference : null,
            group.othersKilos > 0 ? group.totalDifference : null,
          ];

          excelData.push(row);
        });

        // Calcular totales por entrada
        const entryTotals: ExcelShipTotals = {
          mainClientTotalFob: Number(entry.data.mainClient.totalFob),
          mainClientTotalKilos: Number(
            entry.data.mainClient.totalKilos
          ),
          totalOthersFob: 0,
          totalOthersKilos: 0,
          totalShipDifference,
        };

        Object.values(entry.data.comparisons).forEach((clientData) => {
          if (clientData) {
            entryTotals.totalOthersFob = Number(
              (entryTotals.totalOthersFob + (clientData.totalFob || 0))
            );
            entryTotals.totalOthersKilos = Number(
              (
                entryTotals.totalOthersKilos + (clientData.totalKilos || 0)
              )
            );
          }
        });

        // Agregar filas de totales
        const emptyColumns =
          4 + (isFormatEnabled ? 2 : 0) + (isLabelEnabled ? 1 : 0);
        const totalFobKgRow = [
          `${currencyLabel}/KG Promedio`,
          ...Array(emptyColumns - 1).fill(''),
          entryTotals.mainClientTotalFob / entryTotals.mainClientTotalKilos,
          '',
        ];

        const totalKilosRow = [
          'Kilos Totales',
          ...Array(emptyColumns - 1).fill(''),
          '',
          entryTotals.mainClientTotalKilos,
          '',
        ];

        excelData.push(totalFobKgRow);
        excelData.push(totalKilosRow);
        excelData.push(Array(headers.length).fill('')); // Fila vacía

        grandTotalDifference = Number(
          (grandTotalDifference + totalShipDifference)
        );
      }
    });

    // Agregar fila de diferencia total al final
    excelData.push(Array(headers.length).fill(''));
    excelData.push([
      'DIFERENCIA TOTAL',
      ...Array(headers.length - 2).fill(''),
      grandTotalDifference,
    ]);

    return excelData;
  }

  static exportToExcel(
    shipmentData: { [key: string]: GroupedSettlement },
    selectedClient: string,
    isFormatEnabled: boolean,
    isLabelEnabled: boolean,
    isColorEnabled: boolean,
    priceType: 'FOB' | 'CIF' = 'FOB',
    mainClientDetails: MainClientDetailWithComparable[],
    withDetails: boolean = false,
    currencyLabel: string = 'USD'
  ) {
    try {
      const fileName = `Comparativo_Clientes_${selectedClient.replace(
        /\s+/g,
        '_'
      )}.xlsx`;
      const excelData = this.prepareDataForExcel(
        shipmentData,
        selectedClient,
        isFormatEnabled,
        isLabelEnabled,
        isColorEnabled,
        priceType,
        currencyLabel
      );

      // Crear el libro y la hoja
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([], { cellDates: true });

      // Aplicar datos
      XLSX.utils.sheet_add_aoa(ws, excelData, { origin: 'A1' });

      // Aplicar estilos y formato
      const baseColumns = [
        { wch: 20 }, // Nave
        { wch: 15 }, // Variedad
        { wch: 10 }, // Calibre
      ];

      const formatColumns = isFormatEnabled ? [{ wch: 12 }] : []; // Formato
      const labelColumns = isLabelEnabled ? [{ wch: 12 }] : []; // Etiqueta
      const dataColumns = [
        { wch: 15 }, // FOB/CIF KG
        { wch: 15 }, // Kilos
        { wch: 15 }, // FOB/CIF KG (Otros)
        { wch: 15 }, // Kilos (Otros)
        { wch: 15 }, // Diferencia FOB/CIF KG
        { wch: 15 }, // Diferencia Total
      ];

      ws['!cols'] = [
        ...baseColumns,
        ...formatColumns,
        ...labelColumns,
        ...dataColumns,
      ];

      // Agregar la hoja al libro
      XLSX.utils.book_append_sheet(wb, ws, 'Resumen');

      // Add main client details sheet
      if (mainClientDetails && mainClientDetails.length > 0 && withDetails) {
        // Prepare data for the main client details sheet
        const mainClientExcelData: any[][] = [];
        
        // Add header row
        mainClientExcelData.push([
          'Referencia',
          'Nave/Semana',
          'Producto',
          'Kilos',
          `${currencyLabel} Total`,
          `${currencyLabel}/KG`,
          `Comparable ${currencyLabel}/KG`,
          `Diferencia ${currencyLabel}/KG`,
          'Kilos Comparables',
          `Diferencia Total ${currencyLabel}`,
        ]);
        
        // Add data rows
        mainClientDetails.filter(detail => detail.hasComparison).forEach(detail => {
          mainClientExcelData.push([
            detail.reference_number,
            detail.groupKey,
            detail.productKey,
            detail.kilos,
            detail.fob,
            detail.fobPerKg,
            detail.comparisonFobPerKg || '-',
            detail.comparisonFobPerKg ? (detail.fobPerKg - detail.comparisonFobPerKg).toFixed(4) : '-',
            detail.comparableKilos,
            detail.differenceUSD,
            // detail.hasComparison ? 'Sí' : 'No'
          ]);
        });
        
        // Calculate totals
        const totalKilos = mainClientDetails.reduce((sum, detail) => sum + detail.kilos, 0);
        const totalFob = mainClientDetails.reduce((sum, detail) => sum + detail.fob, 0);
        const totalDifferenceUSD = mainClientDetails.reduce((sum, detail) => sum + detail.differenceUSD, 0);
      
        
        // Add totals row
        // mainClientExcelData.push([
        //   'TOTALES',
        //   '',
        //   totalKilos.toFixed(0),
        //   totalFob.toFixed(2),
        //   (totalFob / totalKilos).toFixed(4),
        //   '',
        //   '',
        //   '',
        //   totalDifferenceUSD.toFixed(2),

        // ]);
        
        // Create worksheet
        const wsMainClient = XLSX.utils.aoa_to_sheet(mainClientExcelData, { cellDates: true });
        
        // Set column widths
        wsMainClient['!cols'] = [
          { wch: 20 }, // Referencia
          { wch: 20 }, // Nave/Semana
          { wch: 30 }, // Producto
          { wch: 15 }, // Kilos
          { wch: 15 }, // FOB Total
          { wch: 15 }, // FOB/KG
          { wch: 15 }, // Comparable FOB/KG
          { wch: 15 }, // Diferencia FOB/KG
          { wch: 15 }, // Diferencia Total USD
          { wch: 15 }, // Kilos Comparables
          { wch: 15 }, // FOB Comparable Total
          { wch: 15 }, // Tiene Comparación
        ];
        
        // Add the sheet to the workbook
        XLSX.utils.book_append_sheet(wb, wsMainClient, 'Detalle');
      }

      // Exportar el archivo
      XLSX.writeFile(wb, fileName);

      return true;
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      return false;
    }
  }
}

// InformationTooltip explaining the algorithm
/**
 * Algoritmo de Cálculo de Liquidaciones:
 *
 * 1. Filtrado de Liquidaciones Válidas:
 *    - Se filtran las liquidaciones que tienen una tasa de cambio válida.
 *
 * 2. Agrupación de Liquidaciones:
 *    - Las liquidaciones se agrupan por naves o semanas, dependiendo de la configuración seleccionada.
 *
 * 3. Procesamiento de Grupos:
 *    - Para cada grupo, se calculan los totales de kilos y FOB para el cliente principal y los clientes de comparación.
 *    - Se generan claves únicas para cada detalle de liquidación.
 *
 * 4. Cálculo de Valores Totales:
 *    - Se calcula el valor total basado en el tipo de precio seleccionado (FOB o CIF).
 *    - Se suman los kilos y FOB para cada cliente y se calculan los valores por kilo (FOB/KG).
 *
 * 5. Cálculo de Diferencias:
 *    - Se calculan las diferencias de FOB por kilo entre el cliente principal y los clientes de comparación.
 *    - Estas diferencias se multiplican por los kilos del cliente principal para obtener la diferencia total en FOB.
 *
 * 6. Cálculo de Estadísticas de Variedad:
 *    - Se calculan estadísticas específicas para cada variedad de producto.
 *
 * 7. Generación de Datos para Excel:
 *    - Se preparan los datos para exportar a Excel, incluyendo totales y diferencias.
 *
 * 8. Exportación a Excel:
 *    - Se crea un archivo Excel con los datos preparados, aplicando formatos y estilos.
 */
